import React from 'react';
import styled from '@emotion/styled';
import { graphql } from 'gatsby';
import PropTypes from 'prop-types';

import { containerStyles } from '../../styles/layout';
import { rem } from '../../utilities/style';
import { headers, body } from '../../styles/type';
import { colors } from '../../theme/colors';
import { media } from '../../theme/media';

import TextRenderer from '../TextRenderer';
import Image from '../Image';

const Container = styled.div`
  padding: 80px 0;
`;

const ContentWrapper = styled.div`
  ${containerStyles.regular}
  gap: ${rem(48)};
  justify-content: space-between;

  ${media('>small')} {
    display: flex;
  }
`;

const Stats = styled.div`
  flex: 0 1 ${rem(600)};
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  margin-bottom: ${rem(40)};
  gap: ${rem(20)};
  align-self: start;

  ${media('>small')} {
    margin-bottom: 0;
    gap: ${rem(40)};
  }
`;

const Stat = styled.div`
`;

const StatMetric = styled.p`
  ${headers.h2}
  margin-bottom: ${rem(4)};
`;

const StatText = styled.p`
  ${body.regular}
  color: ${colors.gray['600']};
`;

const ImageContent = styled.div`
  flex: 0 1 ${rem(600)};
  display: flex;
  flex-direction: column;
`;

const Reviews = styled.div`
  display: grid;
  column-gap: ${rem(40)};
  row-gap: ${rem(20)};
  grid-template-columns: repeat(auto-fit, minmax(${rem(90)}, 1fr));
  margin-bottom: auto;
`;

const ReviewWrapper = styled.div`

`;

const Review = styled(Image)`
  height: auto;
  max-height: ${rem(60)};
`;

const Badges = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(${rem(60)}, 1fr));
  column-gap: ${rem(40)};
  row-gap: ${rem(20)};
  margin-top: ${rem(40)};
`;

const BadgeWrapper = styled.div`
  display: flex;
`;

const Badge = styled(Image)`
  height: auto;
  max-height: ${rem(114)};
`;

const StatsBadges = ({
  stat1Metric,
  stat1Text,
  stat2Metric,
  stat2Text,
  stat3Metric,
  stat3Text,
  stat4Metric,
  stat4Text,
  reviews,
  badges
}) => (
  <Container>
    <ContentWrapper>
      <Stats>
        {stat1Metric && stat1Text && (
          <Stat>
            <StatMetric>
              <TextRenderer text={stat1Metric} />
            </StatMetric>
            <StatText>
              <TextRenderer text={stat1Text} />
            </StatText>
          </Stat>
        )}
        {stat2Metric && stat2Text && (
          <Stat>
            <StatMetric>
              <TextRenderer text={stat2Metric} />
            </StatMetric>
            <StatText>
              <TextRenderer text={stat2Text} />
            </StatText>
          </Stat>
        )}
        {stat3Metric && stat3Text && (
          <Stat>
            <StatMetric>
              <TextRenderer text={stat3Metric} />
            </StatMetric>
            <StatText>
              <TextRenderer text={stat3Text} />
            </StatText>
          </Stat>
        )}
        {stat4Metric && stat4Text && (
          <Stat>
            <StatMetric>
              <TextRenderer text={stat4Metric} />
            </StatMetric>
            <StatText>
              <TextRenderer text={stat4Text} />
            </StatText>
          </Stat>
        )}
      </Stats>
      <ImageContent>
        <Reviews>
          {reviews.map((review, index) => (
            // eslint-disable-next-line react/no-array-index-key
            <ReviewWrapper key={index}>
              <Review image={review} />
            </ReviewWrapper>
          ))}
        </Reviews>
        <Badges>
          {badges.map((badge, index) => (
            // eslint-disable-next-line react/no-array-index-key
            <BadgeWrapper key={index}>
              <Badge image={badge} />
            </BadgeWrapper>
          ))}
        </Badges>
      </ImageContent>
    </ContentWrapper>
  </Container>
);

StatsBadges.propTypes = {
  stat1Metric: PropTypes.string,
  stat1Text: PropTypes.string,
  stat2Metric: PropTypes.string,
  stat2Text: PropTypes.string,
  stat3Metric: PropTypes.string,
  stat3Text: PropTypes.string,
  stat4Metric: PropTypes.string,
  stat4Text: PropTypes.string,
  reviews: PropTypes.arrayOf(PropTypes.shape({
    // eslint-disable-next-line react/forbid-prop-types
    gatsbyImageData: PropTypes.object,
    description: PropTypes.string
  })),
  badges: PropTypes.arrayOf(PropTypes.shape({
    // eslint-disable-next-line react/forbid-prop-types
    gatsbyImageData: PropTypes.object,
    description: PropTypes.string
  })),
};

export const query = graphql`
  fragment StatsBadgesFragment on ContentfulStatsBadges {
    id
    __typename
    stat1Metric
    stat1Text
    stat2Metric
    stat2Text
    stat3Metric
    stat3Text
    stat4Metric
    stat4Text
    reviews {
      gatsbyImageData(height: 60, quality: 75)
      ...ImageAssetFragment
    }
    badges {
      gatsbyImageData(height: 114, quality: 75)
      ...ImageAssetFragment
    }
    personalizations {
      ...PersonalizationFragment
    }
  }
`;

export default StatsBadges;
